/**
 * Venue custom-role capability ticks for dashboard access checks.
 *
 * Keep this aligned with express_app/constants/venueRoles.js and
 * express_app/utils/venueRoleCapabilities.js. The dashboard cannot import express.
 *
 * System roles stay on the permission matrix. Custom roles follow these ticks.
 * User management is not a tick — custom roles never manage users.
 */

import {
  CHEF,
  CUSTOM_ROLE_ID_PREFIX,
  HEAD_CHEF_GM,
  KITCHEN_PORTER,
  ORDER_ONLY,
  ROLE_DISPLAY_NAMES,
  ROLE_OPTIONS,
  SYSTEM_ROLE_IDS,
  VENUE_ADMIN,
} from './roles'

export const MISSING_ROLE_FALLBACK = ORDER_ONLY

export const MAX_CUSTOM_ROLES_PER_VENUE = 10
export const MAX_CUSTOM_ROLE_NAME_LENGTH = 40

export const CUSTOM_ROLES_PAID_PLANS = ['Starter', 'Growth', 'Pro']
export const CUSTOM_ROLES_PAID_PLAN_MESSAGE =
  'Custom roles are only available on Starter, Growth or Pro plans.'

export const isCustomRolesPaidPlan = plan => {
  const normalised = String(plan || '')
    .trim()
    .toLowerCase()
  return CUSTOM_ROLES_PAID_PLANS.some(paid => paid.toLowerCase() === normalised)
}

export const CUSTOM_ROLE_NAME_ERROR = {
  REQUIRED: 'NAME_REQUIRED',
  TOO_LONG: 'NAME_TOO_LONG',
  RESERVED: 'NAME_RESERVED',
  DUPLICATE: 'NAME_DUPLICATE',
}

export const CUSTOM_ROLE_NAME_ERROR_MESSAGES = {
  [CUSTOM_ROLE_NAME_ERROR.REQUIRED]: 'Please enter a role name.',
  [CUSTOM_ROLE_NAME_ERROR.TOO_LONG]: 'Role names can be up to 40 characters.',
  [CUSTOM_ROLE_NAME_ERROR.RESERVED]:
    'That name is reserved. Choose a name that is not Admin, Head Chef / GM, Chef, Order Only, Kitchen Porter, or Primary Owner.',
  [CUSTOM_ROLE_NAME_ERROR.DUPLICATE]:
    'A custom role with this name already exists on this venue.',
}

export const RESERVED_CUSTOM_ROLE_NAMES = [
  'admin',
  'venue admin',
  'head chef',
  'head chef gm',
  'head chef / gm',
  'chef',
  'order only',
  'kitchen porter',
  'primary owner',
]

export const VENUE_ROLE_CAPABILITY = {
  ORDERING: 'ordering',
  SUPPLIERS: 'suppliers',
  STOCK_COUNTS: 'stockCounts',
  STOCK_SENSITIVE: 'stockSensitive',
  STOCK_UNLOCK: 'stockUnlock',
  MENU_BUILDER: 'menuBuilder',
  INVOICES: 'invoices',
  REPORTS: 'reports',
  CREDIT_NOTES: 'creditNotes',
  SETTINGS: 'settings',
  SUBSCRIPTIONS: 'subscriptions',
  /** Retrospective editing of already-completed goods receipts. */
  RETROSPECTIVE_RECEIPT_EDIT: 'retrospectiveReceiptEdit',
  /**
   * View and action pending restricted-supplier order approvals from the
   * dashboard Approvals area (in addition to emailed approval links).
   */
  ORDER_APPROVALS: 'orderApprovals',
  /** Complete Food Safety checks/logs and view/export reports. Keep aligned with express_app/constants/venueRoles.js */
  FOOD_SAFETY_COMPLETE: 'foodSafetyComplete',
  /** Create/edit Food Safety definitions and settings. */
  FOOD_SAFETY_MANAGE: 'foodSafetyManage',
}

export const VENUE_ROLE_CAPABILITY_KEYS = Object.values(VENUE_ROLE_CAPABILITY)

export const VENUE_ROLE_CAPABILITY_LABELS = {
  [VENUE_ROLE_CAPABILITY.ORDERING]: 'Order',
  [VENUE_ROLE_CAPABILITY.SUPPLIERS]: 'Suppliers',
  [VENUE_ROLE_CAPABILITY.STOCK_COUNTS]: 'Stock',
  [VENUE_ROLE_CAPABILITY.STOCK_SENSITIVE]: 'Stock variance',
  [VENUE_ROLE_CAPABILITY.STOCK_UNLOCK]: 'Unlock stock',
  [VENUE_ROLE_CAPABILITY.MENU_BUILDER]: 'Menu Builder',
  [VENUE_ROLE_CAPABILITY.INVOICES]: 'GR / Invoices',
  [VENUE_ROLE_CAPABILITY.REPORTS]: 'GP / Reports',
  [VENUE_ROLE_CAPABILITY.CREDIT_NOTES]: 'Credit notes',
  [VENUE_ROLE_CAPABILITY.SETTINGS]: 'Settings',
  [VENUE_ROLE_CAPABILITY.SUBSCRIPTIONS]: 'Subscriptions',
  // Keep aligned with express_app/constants/venueRoles.js
  [VENUE_ROLE_CAPABILITY.RETROSPECTIVE_RECEIPT_EDIT]:
    'Edit previously receipted orders',
  [VENUE_ROLE_CAPABILITY.ORDER_APPROVALS]: 'Approve orders',
  [VENUE_ROLE_CAPABILITY.FOOD_SAFETY_COMPLETE]:
    'Food Safety — complete checks and view EHO reports',
  [VENUE_ROLE_CAPABILITY.FOOD_SAFETY_MANAGE]: 'Food Safety — manage setup',
}

export const VENUE_ROLE_CAPABILITY_SHORT_LABELS = {
  [VENUE_ROLE_CAPABILITY.ORDERING]: 'Order',
  [VENUE_ROLE_CAPABILITY.SUPPLIERS]: 'Suppliers',
  [VENUE_ROLE_CAPABILITY.STOCK_COUNTS]: 'Stock',
  [VENUE_ROLE_CAPABILITY.STOCK_SENSITIVE]: 'Variance',
  [VENUE_ROLE_CAPABILITY.STOCK_UNLOCK]: 'Unlock',
  [VENUE_ROLE_CAPABILITY.MENU_BUILDER]: 'Menu',
  [VENUE_ROLE_CAPABILITY.INVOICES]: 'GR',
  [VENUE_ROLE_CAPABILITY.REPORTS]: 'GP',
  [VENUE_ROLE_CAPABILITY.CREDIT_NOTES]: 'Credits',
  [VENUE_ROLE_CAPABILITY.SETTINGS]: 'Settings',
  [VENUE_ROLE_CAPABILITY.SUBSCRIPTIONS]: 'Subs',
  [VENUE_ROLE_CAPABILITY.RETROSPECTIVE_RECEIPT_EDIT]: 'Edit GR',
  [VENUE_ROLE_CAPABILITY.ORDER_APPROVALS]: 'Approvals',
  [VENUE_ROLE_CAPABILITY.FOOD_SAFETY_COMPLETE]:
    'Food Safety — complete checks and view EHO reports',
  [VENUE_ROLE_CAPABILITY.FOOD_SAFETY_MANAGE]: 'Food Safety — manage setup',
}

export const SYSTEM_ROLE_PICKER_GROUP = 'System roles'
export const CUSTOM_ROLE_PICKER_GROUP = 'Custom'
export const CAPABILITY_CHIP_SEPARATOR = ' · '
export const RESERVED_CUSTOM_ROLE_NAME_HINT =
  "Don't use Admin, Head Chef / GM, Chef, Order Only, Kitchen Porter, or Primary Owner."

export const CUSTOM_ROLES_NEW_BADGE = 'New'
export const CUSTOM_ROLES_HEADING_CUE = 'Make your own role'
export const CUSTOM_ROLES_INVITE_HINT_LEAD =
  "Need a role that isn't Admin, Head Chef / GM, Chef, Order Only, or Kitchen Porter?"
export const CUSTOM_ROLES_INVITE_HINT_ACTION = 'Create a custom role'
export const CUSTOM_ROLES_INVITE_HINT = `${CUSTOM_ROLES_INVITE_HINT_LEAD} ${CUSTOM_ROLES_INVITE_HINT_ACTION}`
export const CUSTOM_ROLES_LEARN_HINT =
  'Need a role that is not Admin, Head Chef / GM, Chef, Order Only, or Kitchen Porter? Create a custom role on this venue and tick what they can access. Only Admins can manage users.'
export const CUSTOM_ROLE_EDIT_START_FROM_HINT =
  'Changing Start from does not reset ticks. Add or remove ticks yourself.'
export const MISSING_CUSTOM_ROLE_ASSIGNMENT_COPY =
  'That custom role was removed. They have Order Only until you pick a new role.'
export const CANNOT_REMOVE_SELF_COPY = 'You cannot remove yourself.'

const allCapabilities = enabled =>
  VENUE_ROLE_CAPABILITY_KEYS.reduce((capabilities, key) => {
    capabilities[key] = enabled
    return capabilities
  }, {})

export const emptyVenueRoleCapabilities = () => allCapabilities(false)

export const SYSTEM_ROLE_CAPABILITIES = {
  [VENUE_ADMIN]: allCapabilities(true),
  [HEAD_CHEF_GM]: {
    ...emptyVenueRoleCapabilities(),
    [VENUE_ROLE_CAPABILITY.ORDERING]: true,
    [VENUE_ROLE_CAPABILITY.SUPPLIERS]: true,
    [VENUE_ROLE_CAPABILITY.STOCK_COUNTS]: true,
    [VENUE_ROLE_CAPABILITY.STOCK_SENSITIVE]: true,
    [VENUE_ROLE_CAPABILITY.STOCK_UNLOCK]: false,
    [VENUE_ROLE_CAPABILITY.MENU_BUILDER]: true,
    [VENUE_ROLE_CAPABILITY.INVOICES]: true,
    [VENUE_ROLE_CAPABILITY.REPORTS]: true,
    [VENUE_ROLE_CAPABILITY.CREDIT_NOTES]: true,
    [VENUE_ROLE_CAPABILITY.SETTINGS]: false,
    [VENUE_ROLE_CAPABILITY.SUBSCRIPTIONS]: false,
    [VENUE_ROLE_CAPABILITY.RETROSPECTIVE_RECEIPT_EDIT]: true,
    [VENUE_ROLE_CAPABILITY.ORDER_APPROVALS]: true,
    [VENUE_ROLE_CAPABILITY.FOOD_SAFETY_COMPLETE]: true,
    [VENUE_ROLE_CAPABILITY.FOOD_SAFETY_MANAGE]: true,
  },
  [CHEF]: {
    ...emptyVenueRoleCapabilities(),
    [VENUE_ROLE_CAPABILITY.ORDERING]: true,
    [VENUE_ROLE_CAPABILITY.SUPPLIERS]: true,
    [VENUE_ROLE_CAPABILITY.STOCK_COUNTS]: true,
    [VENUE_ROLE_CAPABILITY.MENU_BUILDER]: true,
    [VENUE_ROLE_CAPABILITY.FOOD_SAFETY_COMPLETE]: true,
    [VENUE_ROLE_CAPABILITY.FOOD_SAFETY_MANAGE]: false,
  },
  [ORDER_ONLY]: {
    ...emptyVenueRoleCapabilities(),
    [VENUE_ROLE_CAPABILITY.ORDERING]: true,
    [VENUE_ROLE_CAPABILITY.MENU_BUILDER]: true,
    [VENUE_ROLE_CAPABILITY.INVOICES]: true,
    [VENUE_ROLE_CAPABILITY.FOOD_SAFETY_COMPLETE]: true,
    [VENUE_ROLE_CAPABILITY.FOOD_SAFETY_MANAGE]: false,
  },
  [KITCHEN_PORTER]: {
    ...emptyVenueRoleCapabilities(),
    [VENUE_ROLE_CAPABILITY.FOOD_SAFETY_COMPLETE]: true,
  },
}

/** Grey line under a system role when the picker is open. */
export const KITCHEN_PORTER_PICKER_DESCRIPTION = 'Completes Food Safety checks.'

export const SYSTEM_ROLE_PICKER_DESCRIPTIONS = {
  [VENUE_ADMIN]:
    'Runs the venue, including users, the plan, and Food Safety setup.',
  [HEAD_CHEF_GM]:
    'Runs the kitchen, including Food Safety setup. Not users or the plan.',
  [CHEF]:
    'Orders, counts stock, costs recipes, and completes Food Safety checks.',
  [ORDER_ONLY]: 'Places orders and completes Food Safety checks.',
  [KITCHEN_PORTER]: KITCHEN_PORTER_PICKER_DESCRIPTION,
}

/** Custom roles stay in the Free picker, and choosing one opens this lock. */
export const CUSTOM_ROLE_STARTER_LOCK_LABEL = 'Locked to Starter'

const asRoleList = customRoles =>
  Array.isArray(customRoles) ? customRoles : []

export const isSystemRole = roleId => SYSTEM_ROLE_IDS.includes(roleId)

export const isCustomRoleId = roleId =>
  typeof roleId === 'string' &&
  roleId.startsWith(CUSTOM_ROLE_ID_PREFIX) &&
  !isSystemRole(roleId)

export const normalizeSystemRoleId = roleId => {
  if (roleId === 'ADMIN') return VENUE_ADMIN
  if (isSystemRole(roleId)) return roleId
  return null
}

/**
 * Preserve custom role ids (they are case-sensitive `custom_<uuid>`).
 * System roles and the ADMIN alias are uppercased to match dashboard constants.
 * Missing roles still default to Venue Admin, matching useUserRole today.
 */
export const normalizeDashboardVenueRole = role => {
  if (isCustomRoleId(role)) return role
  const normalized = String(role || '')
    .trim()
    .toUpperCase()
  if (normalized === 'ADMIN') return VENUE_ADMIN
  return normalized || VENUE_ADMIN
}

export const cloneCapabilities = capabilities => ({
  ...emptyVenueRoleCapabilities(),
  ...VENUE_ROLE_CAPABILITY_KEYS.reduce((next, key) => {
    if (
      capabilities &&
      Object.prototype.hasOwnProperty.call(capabilities, key)
    ) {
      next[key] = Boolean(capabilities[key])
    }
    return next
  }, {}),
})

export const sanitizeVenueRoleCapabilities = capabilities =>
  cloneCapabilities(capabilities)

export const getSystemRoleCapabilities = roleId => {
  const systemRoleId = normalizeSystemRoleId(roleId)
  if (!systemRoleId) {
    return cloneCapabilities(SYSTEM_ROLE_CAPABILITIES[MISSING_ROLE_FALLBACK])
  }
  return cloneCapabilities(SYSTEM_ROLE_CAPABILITIES[systemRoleId])
}

export const getCapabilitiesFromTemplate = roleId =>
  getSystemRoleCapabilities(roleId)

export const normalizeCustomRoleName = name =>
  String(name || '')
    .trim()
    .toLowerCase()
    .replace(/[_/-]+/g, ' ')
    .replace(/\s+/g, ' ')

export const isReservedCustomRoleName = name => {
  const normalised = normalizeCustomRoleName(name)
  if (!normalised) return false
  return RESERVED_CUSTOM_ROLE_NAMES.includes(normalised)
}

export const isAtCustomRoleLimit = customRoles =>
  asRoleList(customRoles).length >= MAX_CUSTOM_ROLES_PER_VENUE

export const canAddCustomRole = customRoles => !isAtCustomRoleLimit(customRoles)

export const isDuplicateCustomRoleName = (name, customRoles, excludeId) => {
  const normalised = normalizeCustomRoleName(name)
  if (!normalised) return false
  return asRoleList(customRoles).some(
    role =>
      role?.id !== excludeId &&
      normalizeCustomRoleName(role?.name) === normalised,
  )
}

export const validateCustomRoleName = (
  name,
  { existingRoles = [], excludeId } = {},
) => {
  const trimmed = String(name || '').trim()
  if (!trimmed) {
    return { ok: false, error: CUSTOM_ROLE_NAME_ERROR.REQUIRED }
  }
  if (trimmed.length > MAX_CUSTOM_ROLE_NAME_LENGTH) {
    return { ok: false, error: CUSTOM_ROLE_NAME_ERROR.TOO_LONG }
  }
  if (isReservedCustomRoleName(trimmed)) {
    return { ok: false, error: CUSTOM_ROLE_NAME_ERROR.RESERVED }
  }
  if (isDuplicateCustomRoleName(trimmed, existingRoles, excludeId)) {
    return { ok: false, error: CUSTOM_ROLE_NAME_ERROR.DUPLICATE }
  }
  return { ok: true, name: trimmed }
}

export const getCustomRoleNameErrorMessage = error =>
  CUSTOM_ROLE_NAME_ERROR_MESSAGES[error] || ''

export const getBasedOnLabel = basedOn => {
  const systemRoleId = normalizeSystemRoleId(basedOn)
  if (!systemRoleId) return ''
  return `Based on ${ROLE_DISPLAY_NAMES[systemRoleId]}`
}

export const CUSTOM_ROLE_ASSIGNED_DELETE_MESSAGE =
  'This role cannot be deleted while people are assigned to it.'

export const getAssignedCountCopy = count => {
  const n = Math.max(0, Number(count) || 0)
  if (n === 0) return 'Nobody has this role'
  if (n === 1) return '1 person has this role'
  return `${n} people have this role`
}

/** Visible card copy — antd 3 does not show title on a disabled Delete button. */
export const getCustomRoleDeleteBlockedCopy = count => {
  const n = Math.max(0, Number(count) || 0)
  if (n <= 0) return ''
  return `Can't delete — ${getAssignedCountCopy(n)}. Change their role first.`
}

export const lookupAccountVenueRole = (user, venueId) => {
  if (!user) return null
  const map = user.venueRoles
  const venueKey = venueId != null ? String(venueId) : ''
  if (map && typeof map === 'object') {
    if (map[venueId]) return map[venueId]
    if (venueKey && map[venueKey]) return map[venueKey]
    const matchKey = Object.keys(map).find(key => String(key) === venueKey)
    if (matchKey && map[matchKey]) return map[matchKey]
  }
  return user.role || null
}

export const getDisplayedVenueRole = (user, venueId) =>
  normalizeDashboardVenueRole(
    lookupAccountVenueRole(user, venueId) || VENUE_ADMIN,
  )

export const countRoleAssignmentsFromUsers = (users, roleId, venueId) => {
  if (!roleId) return 0
  return (Array.isArray(users) ? users : []).filter(
    user => getDisplayedVenueRole(user, venueId) === roleId,
  ).length
}

/**
 * When user cards are on screen, count from those users so “1 person” cannot
 * disagree with every card still saying Admin after a custom-role list refresh.
 * Fall back to API assignedCount only when no user list is provided.
 */
export const resolveAssignedCount = (role, { users, venueId } = {}) => {
  if (Array.isArray(users)) {
    return countRoleAssignmentsFromUsers(users, role?.id, venueId)
  }
  if (role?.assignedCount != null && role.assignedCount !== '') {
    return Math.max(0, Number(role.assignedCount) || 0)
  }
  return 0
}

export const canDeleteCustomRole = (role, options) =>
  resolveAssignedCount(role, options) === 0

export const getEnabledCapabilityChips = capabilities => {
  const safe = sanitizeVenueRoleCapabilities(capabilities)
  return VENUE_ROLE_CAPABILITY_KEYS.filter(key => safe[key]).map(key => ({
    key,
    label: VENUE_ROLE_CAPABILITY_LABELS[key],
  }))
}

export const formatCapabilityChipSummary = capabilities =>
  getEnabledCapabilityChips(capabilities)
    .map(chip => VENUE_ROLE_CAPABILITY_SHORT_LABELS[chip.key])
    .filter(Boolean)
    .join(CAPABILITY_CHIP_SEPARATOR)

export const getRoleCapabilityChips = (roleId, customRoles = []) =>
  getEnabledCapabilityChips(resolveVenueRole(roleId, customRoles).capabilities)

export const getVenueRoleDisplayName = (roleId, customRoles = []) =>
  resolveVenueRole(roleId, customRoles).displayName

/**
 * Picker value only. A deleted custom id still lives on the account until
 * someone picks a new role — do not persist this rewrite on load.
 */
export const getVenueRoleSelectValue = (roleId, customRoles = []) =>
  resolveVenueRole(roleId, customRoles).id

export const getMissingCustomRoleAssignmentCopy = (
  roleId,
  customRoles = [],
) => {
  if (!isCustomRoleId(roleId)) return ''
  if (!resolveVenueRole(roleId, customRoles).isFallback) return ''
  return MISSING_CUSTOM_ROLE_ASSIGNMENT_COPY
}

export const getVenueRoleCardChipSummary = (roleId, customRoles = []) => {
  const resolved = resolveVenueRole(roleId, customRoles)
  if (resolved.kind !== 'custom' || resolved.isFallback) return ''
  return formatCapabilityChipSummary(resolved.capabilities)
}

export const getRolePickerGroups = (
  customRoles = [],
  { lockCustomRoles = false } = {},
) => {
  const customOptions = asRoleList(customRoles)
    .filter(role => role?.id && isCustomRoleId(role.id))
    .map(role => {
      const label = String(role.name || '').trim() || 'Custom role'
      return {
        value: role.id,
        label,
        chipSummary: formatCapabilityChipSummary(role.capabilities),
        locked: lockCustomRoles,
        description: lockCustomRoles ? CUSTOM_ROLE_STARTER_LOCK_LABEL : '',
      }
    })

  const groups = [
    {
      label: SYSTEM_ROLE_PICKER_GROUP,
      options: ROLE_OPTIONS.map(option => ({
        value: option.value,
        label: option.label,
        description: SYSTEM_ROLE_PICKER_DESCRIPTIONS[option.value] || '',
      })),
    },
  ]

  if (customOptions.length) {
    groups.push({
      label: CUSTOM_ROLE_PICKER_GROUP,
      options: customOptions,
    })
  }

  return groups
}

export const findCustomRole = (roleId, customRoles) => {
  if (!isCustomRoleId(roleId)) return null
  return asRoleList(customRoles).find(role => role?.id === roleId) || null
}

const fallbackResolvedRole = () => ({
  kind: 'system',
  id: MISSING_ROLE_FALLBACK,
  displayName: ROLE_DISPLAY_NAMES[MISSING_ROLE_FALLBACK],
  basedOn: null,
  capabilities: getSystemRoleCapabilities(MISSING_ROLE_FALLBACK),
  isFallback: true,
})

export const resolveVenueRole = (roleId, customRoles = []) => {
  const systemRoleId = normalizeSystemRoleId(roleId)
  if (systemRoleId) {
    return {
      kind: 'system',
      id: systemRoleId,
      displayName: ROLE_DISPLAY_NAMES[systemRoleId],
      basedOn: null,
      capabilities: getSystemRoleCapabilities(systemRoleId),
      isFallback: false,
    }
  }

  const customRole = findCustomRole(roleId, customRoles)
  if (customRole) {
    const basedOn = normalizeSystemRoleId(customRole.basedOn)
    const name = String(customRole.name || '').trim() || 'Custom role'
    return {
      kind: 'custom',
      id: customRole.id,
      displayName: name,
      basedOn: basedOn || null,
      capabilities: sanitizeVenueRoleCapabilities(customRole.capabilities),
      isFallback: false,
    }
  }

  return fallbackResolvedRole()
}

/** Same words as Express (constants/venueRoles.js) and the app. */
export const VENUE_ROLE_CHANGE_HINT =
  'ask your Admin to change it in Settings → Users'

/** "Your role here is Order Only — ask your Admin to change it in Settings → Users." */
export const getVenueRoleRefusalCopy = (roleId, customRoles = []) =>
  `Your role here is ${
    resolveVenueRole(roleId, customRoles).displayName
  } — ${VENUE_ROLE_CHANGE_HINT}.`

export const hasCapability = (roleId, capability, customRoles = []) => {
  if (!VENUE_ROLE_CAPABILITY_KEYS.includes(capability)) return false
  const resolved = resolveVenueRole(roleId, customRoles)
  return Boolean(resolved.capabilities[capability])
}

export const canCompleteFoodSafety = (roleId, customRoles = []) =>
  hasCapability(roleId, VENUE_ROLE_CAPABILITY.FOOD_SAFETY_COMPLETE, customRoles)

export const canManageFoodSafety = (roleId, customRoles = []) =>
  hasCapability(roleId, VENUE_ROLE_CAPABILITY.FOOD_SAFETY_MANAGE, customRoles)

/**
 * Invite / change-role / remove. Custom roles never get this, even with every tick.
 * Primary Owner is treated as Venue Admin by callers, not by this helper.
 * This is not OMS / TFR / GREEN / JUST Admin.
 */
export const canManageVenueUsers = roleId => {
  if (isCustomRoleId(roleId)) return false
  const systemRoleId =
    normalizeSystemRoleId(roleId) ||
    normalizeSystemRoleId(
      String(roleId || '')
        .trim()
        .toUpperCase(),
    )
  return systemRoleId === VENUE_ADMIN
}
